/**
 * Which services of a project are deployed: Apps' "Deploy this service" switch, ON for every service unless switched
 * off (a design mockup, a tool, a test harness). Saved in the project's `.flow/project.json` (utils/defaultApp's
 * settings file, other keys kept) as `"deploy": { "<service>": false }`, only the services switched off; vfs-service
 * reads the same key (src/preview/deployFlags.js) for the release gate and Publish:
 * - the Quality gate counts only deployed services; the others are still checked, "Not deployed: doesn't block Publish",
 *   except secrets and licences for Git and GitOps (the code goes to the Git remote whatever the switch);
 * - Publish to Console, the image registry, the Helm chart and GitOps leave them out;
 * - Publish to Git and Download carry every service's code.
 * One answer per project, shared by every caller (useDeployFlags).
 */
import { readProjectSettings, updateProjectSettings } from './defaultApp';
import { notifyQualityChanged } from '../services/QualityService';

const FIELD = 'deploy';

export const DEPLOY_ON_HELP = 'Published with the project: Console, the image registry, the Helm chart and GitOps include it.';
export const DEPLOY_OFF_HELP = 'Not deployed: Console, the image registry, the Helm chart and GitOps leave it out, and its Quality results don\'t block Publish, except secrets and licences for Git and GitOps. Git and Download still include its code.';
export const NOT_DEPLOYED_TIP = 'Not deployed: Deploy is off for this service (Apps). Its checks don\'t block Publish, except secrets and licences for Git and GitOps.';

const shortName = (name) => String(name || '').replace(/^services\//, '').replace(/\/+$/, '');

/** The one-line confirmation before Deploy goes off for `service` (Apps' switch). */
export const deployOffConfirm = (service) => ({
  title: `Stop deploying ${shortName(service)}?`,
  line: `Console, the image registry, the Helm chart and GitOps will leave ${shortName(service)} out; Git and Download still include its code.`,
  ok: 'Don\'t deploy',
});

const mapOf = (settings) => (settings?.[FIELD] && typeof settings[FIELD] === 'object' && !Array.isArray(settings[FIELD]) ? settings[FIELD] : {});

/** The services switched off in these settings (short names, sorted). */
export function deployOff(settings) {
  const map = mapOf(settings);
  return Object.keys(map).filter((name) => map[name] === false).sort();
}

/** The settings with `service` deployed (`on`) or not, other keys kept; no service left off: no key. */
export function withDeploy(settings, service, on) {
  const next = { ...(settings || {}) };
  const map = { ...mapOf(next) };
  if (on) { delete map[shortName(service)]; } else { map[shortName(service)] = false; }
  if (Object.keys(map).length) { next[FIELD] = map; } else { delete next[FIELD]; }
  return next;
}

/** "2 of 3 services will be deployed (design-mockup is off)" for these short names. */
export function deploySentence(services, off) {
  const names = [...new Set((services || []).map(shortName).filter(Boolean))];
  const offNames = names.filter((name) => (off || []).includes(name));
  if (!names.length) { return 'No service to deploy yet'; }
  const head = `${names.length - offNames.length} of ${names.length} ${names.length === 1 ? 'service' : 'services'} will be deployed`;
  return offNames.length ? `${head} (${offNames.join(', ')} ${offNames.length === 1 ? 'is' : 'are'} off)` : head;
}

const entries = new Map();
const entryOf = (projectId) => {
  if (!entries.has(projectId)) { entries.set(projectId, { off: [], loaded: false, loading: null, listeners: new Set() }); }
  return entries.get(projectId);
};
const publish = (projectId, off) => {
  const entry = entryOf(projectId);
  entry.off = off;
  entry.loaded = true;
  entry.listeners.forEach((listener) => listener(off));
};

/** The services known to be off now (short names). */
export const deployOffOf = (projectId) => (projectId ? entryOf(projectId).off : []);
export const deployLoaded = (projectId) => Boolean(projectId && entryOf(projectId).loaded);

/** Calls `listener(off)` when the project's switches change; returns the unsubscribe. */
export function subscribeDeployFlags(projectId, listener) {
  const entry = entryOf(projectId);
  entry.listeners.add(listener);
  return () => { entry.listeners.delete(listener); };
}

/** Reads `.flow/project.json` once per project (again with `force`). Never rejects: no file means every service on. */
export async function loadDeployFlags(projectId, { force = false } = {}) {
  if (!projectId) { return []; }
  const entry = entryOf(projectId);
  if (entry.loading) { return entry.loading; }
  if (entry.loaded && !force) { return entry.off; }
  entry.loading = readProjectSettings(projectId)
    .then((settings) => { publish(projectId, deployOff(settings || {})); return entry.off; })
    .catch(() => { publish(projectId, entry.off); return entry.off; })
    .finally(() => { entry.loading = null; });
  return entry.loading;
}

/** Switches Deploy of `service` on or off; resolves to the services off; rejects when the file cannot be written. */
export async function saveDeploy(projectId, service, on) {
  const next = await updateProjectSettings(projectId, (current) => withDeploy(current, service, on));
  const off = deployOff(next);
  publish(projectId, off);
  // The gate and the Publish dialog read it again (the gate counts only deployed services).
  notifyQualityChanged(projectId);
  return off;
}

/** Test helper. */
export function resetDeployFlags() { entries.clear(); }
